import { findCustomFieldByIntegrationName } from "@/react/shared/custom-field-utils";
import { GeneratedReportStore } from "@/stores/generated-report-store.core";
import {
   type CommonReportParams,
   GenerateReportType,
   ReportType,
   type ReportResponse,
} from "@/react/prop-types";
import { ColumnEntityType } from "@laborchart-modules/common";
import { Order } from "@laborchart-modules/common/dist/reql-builder/query-definitions";
import { mapServerFilters } from "@/react/shared/helper";
import type { ListServerFilter } from "../data-table/data-table-utils";
import type { FindRequestsSortBy } from "@laborchart-modules/common/dist/reql-builder/procedures/find-requests";
import {
   convertRequestDataTableFilters,
   fieldTranslationKeyMap,
   legacyFieldNamesMap,
   sortFieldMaps,
} from "./request-list-helpers";
import type { NotifiableError } from "@bugsnag/js";
import Bugsnag from "@bugsnag/js";
import { BUGSNAG_META_TAB, buildUserData } from "@/lib/utils/bugsnag-content-helper";
import { authManager } from "@/lib/managers/auth-manager";

export async function createRequestListReport(params: CommonReportParams): Promise<ReportResponse> {
   const { configKey, customFields, groupId, formValues, I18n, isLastNameFirst, search } = params;
   let customFieldId: string | null = null;
   let sortBy: FindRequestsSortBy = "id";
   let payload: any = {};

   try {
      const localStorageTableConfig = JSON.parse(localStorage.getItem(configKey) ?? "");
      const sortField = localStorageTableConfig.columnState.find((col: any) => col.sort !== null);

      // check if the table is grouped and sorting is applied on the grouped column
      const isGrouped = localStorageTableConfig.columnState.some((col: any) => col.rowGroup);
      const groupColumnSort = localStorageTableConfig.columnState.find(
         (col: any) => col.field === "ag-Grid-AutoColumn" && col.sort !== null,
      );

      // as of now only project is supported for grouped data
      if (isGrouped && groupColumnSort) {
         sortBy = sortFieldMaps["project"];
      } else if (sortField) {
         const customField = findCustomFieldByIntegrationName(customFields!, sortField.field);
         if (customField) {
            customFieldId = customField.id;
         }
         sortBy = sortFieldMaps[sortField.field];
      }

      const column_headers = localStorageTableConfig.columnState
         .filter((col: any) => (!col.hidden || col.rowGroup) && col.field !== "ag-Grid-AutoColumn") //consider col.rowGroup to include grouped data in export
         .map((col: any, i: number) => {
            const customField = findCustomFieldByIntegrationName(customFields!, col.field);

            let meta;

            const key = customField ? "custom_fields" : legacyFieldNamesMap[col.field];
            const name = customField ? customField.name : I18n.t(fieldTranslationKeyMap[col.field]);
            const base_name = customField ? customField.integration_name : null;

            // Add meta object when customField exists
            if (customField) {
               meta = {
                  field_id: customField.id,
                  field_property: customField.integration_name,
                  field_type: customField.type,
                  field_entity: ColumnEntityType.REQUESTS,
               };
            }

            return {
               key,
               name,
               width: Math.round(col.width), // column width can be a float, so round it
               sequence: i,
               base_name,
               sortable: true,
               sub_properties: col.rowGroup ? [{ key: key, enabled: col.rowGroup, name: key }] : [],
               ...(customField && { meta }), // Add meta field only if customField exists
            };
         });

      const updatedServerFilters = mapServerFilters(
         localStorageTableConfig.serverFilters,
         customFields!,
      );

      payload = {
         sort_by: sortBy,
         sort_direction:
            (isGrouped && groupColumnSort ? groupColumnSort.sort : sortField?.sort) == "desc"
               ? Order.DESCENDING
               : Order.ASCENDING,
         timezone: Intl.DateTimeFormat().resolvedOptions().timeZone, // This is how legacy report generation gets TZ
         report_type: ReportType.RequestList,
         group_id: groupId,
         limit: 40,
         filters: convertRequestDataTableFilters(updatedServerFilters as ListServerFilter[]),
         page_size: {
            height:
               formValues.orientation.id == "portrait"
                  ? formValues.page_size.value.height
                  : formValues.page_size.value.width,
            width:
               formValues.orientation.id == "portrait"
                  ? formValues.page_size.value.width
                  : formValues.page_size.value.height,
         },
         display_last_names_first: isLastNameFirst,
         file_type: formValues.file_type.value,
         isCombinedRequests: true,
         column_headers,
         ...(search && { search }), // add search if it exists
         ...(customFieldId && { custom_field_id: customFieldId }), // add custom_field_id if it exists
      };
      const report = await GeneratedReportStore.create(GenerateReportType.RequestList, payload)
         .payload;

      return report;
   } catch (error) {
      Bugsnag.notify(`error in generating request list report` as NotifiableError, (event) => {
         const user = authManager.authedUser();
         if (user) {
            event.addMetadata(
               BUGSNAG_META_TAB.USER_DATA,
               buildUserData(user, authManager.activePermission),
            );
         }
         event.severity = "error";
         event.context = "Helix Request List Report Generation";
         event.addMetadata("payload", { data: payload });
      });
      throw error;
   }
}
